import { arrayOf, bool, element, func, instanceOf, oneOfType, string } from 'prop-types';
import React, { forwardRef, useEffect } from 'react';
import ChildrenWrapper from './mask/children-wrapper';
import { useInputElement, useInputState, usePrevious } from './mask/hooks';
import { defer } from './mask/utils/defer';
import { toString } from './mask/utils/helpers';
import { isInputFocused } from './mask/utils/input';
import MaskUtils from './mask/utils/mask';
import { validateChildren, validateMaskPlaceholder, validateMaxLength } from './mask/validate-props';
const InputMask = WrappedComponent => forwardRef((props, forwardedRef) => {
  const {
    alwaysShowMask = false,
    children,
    mask,
    maskPlaceholder = ' ',
    beforeMaskedStateChange,
    ...restProps
  } = props;
  validateMaxLength(props);
  validateMaskPlaceholder(props);
  const maskUtils = new MaskUtils({
    mask,
    maskPlaceholder
  });
  const isMasked = !!mask;
  const isEditable = !restProps.disabled && !restProps.readOnly;
  const isControlled = props.value !== null && props.value !== undefined;
  const previousIsMasked = usePrevious(isMasked);
  const initialValue = toString((isControlled ? props.value : props.defaultValue) || '');
  const {
    inputRef,
    getInputState,
    setInputState,
    getLastInputState
  } = useInputState(initialValue, isMasked, forwardedRef);
  const getInputElement = useInputElement(inputRef);
  function onChange(event) {
    const currentState = getInputState();
    const previousState = getLastInputState();
    let newInputState = maskUtils.processChange(currentState, previousState);
    if (beforeMaskedStateChange) {
      newInputState = beforeMaskedStateChange({
        currentState,
        previousState,
        nextState: newInputState
      });
    }
    setInputState(newInputState);
    if (props.onChange) {
      props.onChange(event);
    }
  }
  function onFocus(event) {
    const currentValue = getInputState().value;
    if (isMasked && !maskUtils.isValueFilled(currentValue)) {
      let newValue = maskUtils.formatValue(currentValue);
      let newSelection = maskUtils.getDefaultSelectionForValue(newValue);
      let newInputState = {
        value: newValue,
        selection: newSelection
      };
      if (beforeMaskedStateChange) {
        newInputState = beforeMaskedStateChange({
          currentState: getInputState(),
          nextState: newInputState
        });
        newValue = newInputState.value;
        newSelection = newInputState.selection;
      }
      setInputState(newInputState);
      if (newValue !== currentValue && props.onChange) {
        props.onChange(event);
      }
      defer(() => {
        setInputState(getLastInputState());
      });
    }
    if (props.onFocus) {
      props.onFocus(event);
    }
  }
  function onBlur(event) {
    const currentValue = getInputState().value;
    const lastValue = getLastInputState().value;
    if (isMasked && !alwaysShowMask && maskUtils.isValueEmpty(lastValue)) {
      let newValue = '';
      let newInputState = {
        value: newValue,
        selection: {
          start: null,
          end: null
        }
      };
      if (beforeMaskedStateChange) {
        newInputState = beforeMaskedStateChange({
          currentState: getInputState(),
          nextState: newInputState
        });
        newValue = newInputState.value;
      }
      setInputState(newInputState);
      if (newValue !== currentValue && props.onChange) {
        props.onChange(event);
      }
    }
    if (props.onBlur) {
      props.onBlur(event);
    }
  }
  function onMouseDown(event) {
    const input = getInputElement();
    const {
      value
    } = getInputState();
    if (!isInputFocused(input) && !maskUtils.isValueFilled(value)) {
      const mouseDownX = event.clientX;
      const mouseDownY = event.clientY;
      const mouseDownTime = new Date().getTime();
      const mouseUpHandler = mouseUpEvent => {
        document.removeEventListener('mouseup', mouseUpHandler);
        if (!isInputFocused(input)) {
          return;
        }
        const deltaX = Math.abs(mouseUpEvent.clientX - mouseDownX);
        const deltaY = Math.abs(mouseUpEvent.clientY - mouseDownY);
        const axisDelta = Math.max(deltaX, deltaY);
        const timeDelta = new Date().getTime() - mouseDownTime;
        if (axisDelta <= 10 && timeDelta <= 200 || axisDelta <= 5 && timeDelta <= 300) {
          const lastState = getLastInputState();
          const newSelection = maskUtils.getDefaultSelectionForValue(lastState.value);
          const newState = {
            ...lastState,
            selection: newSelection
          };
          setInputState(newState);
        }
      };
      document.addEventListener('mouseup', mouseUpHandler);
    }
    if (props.onMouseDown) {
      props.onMouseDown(event);
    }
  }
  if (isMasked && isControlled) {
    const input = getInputElement();
    const isFocused = input && isInputFocused(input);
    let newValue = isFocused || alwaysShowMask || props.value ? maskUtils.formatValue(props.value) : props.value;
    if (beforeMaskedStateChange) {
      newValue = beforeMaskedStateChange({
        nextState: {
          value: newValue,
          selection: {
            start: null,
            end: null
          }
        }
      }).value;
    }
    setInputState({
      ...getLastInputState(),
      value: newValue
    });
  }
  const lastState = getLastInputState();
  const lastSelection = lastState.selection;
  const lastValue = lastState.value;
  useEffect(() => {
    if (!isMasked) {
      return;
    }
    const input = getInputElement();
    const isFocused = isInputFocused(input);
    const previousSelection = lastSelection;
    const currentState = getInputState();
    let newInputState = {
      ...currentState
    };
    if (!isControlled) {
      const currentValue = currentState.value;
      const formattedValue = maskUtils.formatValue(currentValue);
      const isValueEmpty = maskUtils.isValueEmpty(formattedValue);
      const shouldFormatValue = !isValueEmpty || isFocused || alwaysShowMask;
      if (shouldFormatValue) {
        newInputState.value = formattedValue;
      } else if (isValueEmpty && !isFocused) {
        newInputState.value = '';
      }
    }
    if (isFocused && !previousIsMasked) {
      newInputState.selection = maskUtils.getDefaultSelectionForValue(newInputState.value);
    } else if (isControlled && isFocused && previousSelection) {
      if (previousSelection.start !== null && previousSelection.end !== null) {
        newInputState.selection = previousSelection;
      }
    }
    if (beforeMaskedStateChange) {
      newInputState = beforeMaskedStateChange({
        currentState,
        nextState: newInputState
      });
    }
    setInputState(newInputState);
  });
  const inputProps = {
    ...restProps,
    onFocus,
    onBlur,
    onChange: isMasked && isEditable ? onChange : props.onChange,
    onMouseDown: isMasked && isEditable ? onMouseDown : props.onMouseDown,
    ref: inputRef,
    value: isMasked && isControlled ? lastValue : props.value
  };
  if (children) {
    validateChildren(props, children);
    return React.createElement(ChildrenWrapper, inputProps, children);
  }
  return React.createElement(WrappedComponent, inputProps);
});
InputMask.displayName = 'InputMask';
InputMask.propTypes = {
  alwaysShowMask: bool,
  beforeMaskedStateChange: func,
  children: element,
  mask: oneOfType([string, arrayOf(oneOfType([string, instanceOf(RegExp)]))]),
  maskPlaceholder: string,
  onFocus: func,
  onBlur: func,
  onChange: func,
  onMouseDown: func
};
export default InputMask;
//# sourceMappingURL=InputMask.js.map